/**
 * RiskFactor.js
 * Created by Federico on 08/06/2021.
 */

import React, {useState, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {riskFactorShape} from '../../shapes';
import {labels} from '../../../../../util/utils';
import {clientSideEventNames, formBodyType} from '../../../../../util/uiconstants';
import Utility from '../../../../../util/utilityfunctions.js';
import * as RiskFactorActions from '../../../../../actions/RiskFactor/riskfactoractions';
import EllipseControl from '../../../EllipsesControl/EllipsesControl';
import ShowMoreLess from '../../../ShowMoreLess/ShowMoreLess';
import RiskFactorDescriptionDropdownContainer from '../Common/RiskFactorDescriptionDropdownContainer';
import Label from '../../../BaseComponentWrappers/MotifLabel';
import ProgressBar from '../../../BaseComponentWrappers/MotifProgressBar';
import {useEngagementId, useDocumentId} from '../../../../../util/customHooks';

export default function RiskFactor({
	riskFactor,
	sectionId,
	bodyId,
	isViewBodyOnly,
	showMoreControl,
	bodyTypeId,
	isDisabled
}) {
	const myDescriptions = useSelector((state) => state.aresMyRiskFactorDescriptions);
	const documentId = useDocumentId();

	const storeRiskFactor = useSelector((state) => {
		return RiskFactorActions.findRiskFactorFromFormData(
			state.formBodyData,
			bodyId,
			riskFactor.formBodyOptionId,
			riskFactor.id,
			sectionId
		);
	});

	const dispatch = useDispatch();
	let inputValue = riskFactor?.customDescription;

	const [inputEdit, setInputEdit] = useState(false),
		[text, setRichText] = useState(inputValue),
		[storeRiskFactors, setStoreRiskFactors] = useState([]);

	const engagementId = useEngagementId();

	const selectedDescription = useMemo(() => {
		if (myDescriptions?.riskFactors?.allIds.length > 0) {
			return RiskFactorActions.findRiskFactorDescription(myDescriptions, riskFactor.id);
		}
	}, [myDescriptions]);

	const formBodyData = useSelector((store) => store.formBodyData);

	const getValue = () => {
		if (!inputEdit) {
			let formatedText = text === null || text === '' ? (isViewBodyOnly ? labels.noResponseAvailable : '') : text;

			if (isViewBodyOnly) {
				return (
					<EllipseControl
						content={Utility.createRichTextElement(formatedText, isViewBodyOnly ? 'rationaleText' : '')}
						tooltip={Utility.createRichTextElement(formatedText, isViewBodyOnly ? 'rationaleText' : '')}
						noOfLines={3}
					/>
				);
			} else {
				return Utility.createRichTextElement(formatedText, isViewBodyOnly ? 'rationaleText' : '');
			}
		} else {
			return text ? text.trim() : '';
		}
	};

	const saveDescription = (riskFactor, description) => {
		if (riskFactor.riskFactorRelationId) {
			return dispatch(
				RiskFactorActions.updateRiskFactorRelationDescription(
					engagementId,
					riskFactor.id,
					riskFactor.riskFactorRelationId,
					riskFactor.formSectionId,
					riskFactor.formBodyId,
					riskFactor.formBodyOptionId,
					description,
					documentId
				)
			).then(() => {
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
			});
		} else {
			return dispatch(
				RiskFactorActions.createRiskFactorRelation(
					engagementId,
					riskFactor.formBodyOptionId,
					riskFactor.formBodyOptionActionId,
					description,
					riskFactor.id,
					riskFactor.formSectionId,
					riskFactor.formBodyId,
					documentId
				)
			).then(() => {
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
			});
		}
	};

	const saveInputEdit = (description, mode) => {
		setInputEdit(false);
		setRichText(description);
		document.getElementById(riskFactor.id).classList.add('greyOutArea');

		if (bodyTypeId && bodyTypeId === formBodyType.CreateRiskFactorBody) {
			window.dispatchEvent(
				new CustomEvent(clientSideEventNames.saveBody, {
					detail: {
						text: description,
						riskId: riskFactor.id,
						storeRiskFactors: storeRiskFactors
					}
				})
			);
		}

		const clearOutLoaderIndicator = () => $('.greyOutArea').removeClass('greyOutArea');
		try {
			let storeDesc;
			let _riskFactor;

			//If we edit a description (which may be shared between risk factors), we need to update them all.
			if (RiskFactorActions.dropdownActions.edit === mode) {
				storeDesc = myDescriptions.descriptions.byId[selectedDescription];

				return Promise.all(
					storeDesc.map((riskFactorId) => {
						_riskFactor = myDescriptions.riskFactors.byId[riskFactorId];
						return saveDescription(_riskFactor, description);
					})
				).finally(clearOutLoaderIndicator);
			} else {
				_riskFactor = {...storeRiskFactor, formSectionId: sectionId, formBodyId: bodyId};

				if (RiskFactorActions.dropdownActions.remove === mode) {
					return dispatch(
						RiskFactorActions.removeRiskFactorRelation(
							engagementId,
							_riskFactor.formBodyOptionId,
							_riskFactor.id,
							_riskFactor.formSectionId,
							_riskFactor.formBodyId,
							_riskFactor.riskFactorRelationId,
							documentId
						)
					)
						.then(() => {
							window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
						})
						.finally(clearOutLoaderIndicator);
				} else {
					//If we relate a risk factor to a new description or remove it, we just trigger the single api put call.
					return saveDescription(_riskFactor, description).finally(clearOutLoaderIndicator);
				}
			}
		} catch {
			clearOutLoaderIndicator();
		}
	};

	useEffect(() => {
		let riskFactors = [];
		if (bodyTypeId && bodyTypeId === formBodyType.CreateRiskFactorBody && formBodyData && formBodyData.length > 0) {
			let myBodies = formBodyData.find((bodies) => {
				return bodies.sectionId === sectionId;
			});
			if (myBodies) {
				let body = myBodies.bodies.find((b) => {
					return Number(b.id) === Number(bodyId);
				});

				if (body) {
					riskFactors = body.optionsAndResponses[0].riskFactorList;
				}
			}
			setStoreRiskFactors(riskFactors);
		}
	}, [formBodyData]);

	return (
		<>
			<section className="RiskLabel">
				{' '}
				{labels.riskFactorLabel}: {riskFactor ? riskFactor.riskFactorName : ''}
			</section>
			{!isViewBodyOnly ? (
				<section className="RiskInputField" id={riskFactor.id}>
					<section className="MotifProgressBarWrapper">
						<ProgressBar isIndeterminate circle hideLabel />
					</section>
					<RiskFactorDescriptionDropdownContainer
						engagementId={engagementId}
						riskFactorId={riskFactor.id}
						riskFactorRelationId={storeRiskFactor?.riskFactorRelationId}
						onSave={saveInputEdit}
						isDisabled={isDisabled}
					/>
				</section>
			) : (
				<>
					{showMoreControl ? (
						<ShowMoreLess
							showMoreHeight={showMoreControl}
							onClick={(e) => {
								e.stopPropagation();
							}}
						>
							<Label className={`${text === null || text === '' ? 'NoResponseLabel' : 'ResponseLabel'}`}>
								{getValue()}
							</Label>
						</ShowMoreLess>
					) : (
						<Label className={`${text === null || text === '' ? 'NoResponseLabel' : 'ResponseLabel'}`}>
							{getValue()}
						</Label>
					)}
				</>
			)}
		</>
	);
}

RiskFactor.propTypes = {
	riskFactor: riskFactorShape,
	sectionId: PropTypes.number.isRequired,
	bodyId: PropTypes.number.isRequired,
	isViewBodyOnly: PropTypes.bool,
	showMoreControl: PropTypes.bool
};
